import { GridRenderCellParams } from "@mui/x-data-grid";

import { Slider, styled } from "@mui/material";
import { OutletCollectionDetails } from "../../types/types";
import RenderSvg from "../../components/renderSvg/RenderSvg";

export interface PaymentDetail {
  mode: string;
  amount: number;
  transactionId?: string;
  chequeDate?: string;
  bankAccountNo?: string;
  bankName?: string;
  ifscCode?: string;
  payeeName?: string;
}

export interface PaymentDetailsProps {
  paymentDetail: PaymentDetail;
  parentIndex: number;
  isOpen: boolean;
  onToggle: () => void;
  onUpdate: (updatedDetail: PaymentDetail, index: number) => void;
  totalAmount: number;
  collectedAmount: number;
  onOpenModal: (message: string) => void;
}

export interface CreditNoteDetails {
  creditNoteNumber: string;
  amount: number;
  issueDate: string;
  returnId: string;
  startDate: string;
  endDate: string;
}

export interface CreditNoteData {
  id: string;
  amount: number;
  noteNumber: string;
  availableAmount: number;
  expired: boolean;
  endDate: string; 
}

// Define the interface for the PaymentCard props
export interface PaymentCardProps {
  paymentDate: string;
  paidAmount: string;
  finalAmount: string;
  paymentMode: string;
  shortageAmount: string; // Add shortageAmount as a prop
  handleShortageChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Add handler for input change
  handleConfirm: () => void; // Add handler for confirm action
  handleCancel: () => void; // Add handler for cancel action
  onShortageSubmit?: (shortageAmount: string) => void;
}

export interface CollectionProps {
  paidByCash: number;
  paidByUpi: number;
  paidByCheque: number;
  paidByNeft: number;
}
export interface InvoiceData {
  invoiceNumber: string;
  status: string;
  pendingAmount: number;
}

export const collectionMetaData1: any[] = [
  {
    keys: "outletName",
    label: "Outlet Name",
    index: 1,
    removeEllipsis: true,
    truncateTextAfterChar: 60,
  },

  {
    keys: "outletType",
    label: "Customer Type",
    index: 2,
    removeEllipsis: true,
  },
  {
    keys: "creditLimit",
    label: "Credit Limit",
    index: 3,
    valueType: "Number",
    currencySymbol: "₹",
    removeEllipsis: true,
  },
];

export const collectionMetaData2: any[] = [
  {
    keys: "creditAmount",
    label: "Outstanding",
    index: 1,
    valueType: "Number",
    currencySymbol: "₹",
    removeEllipsis: true,
  },
  {
    keys: "totalApprovedPaidAmount",
    label: "Paid Amount",
    index: 2,
    valueType: "Number",
    currencySymbol: "₹",
    removeEllipsis: true,
  },

  {
    keys: "creditDueDate",
    label: "Payment Due Date",
    index: 3,
    removeEllipsis: true,
  },
];

export const paymentTableColumnsMetaData = {
  columns: [
    {
      headerName: "Payment Date",
      mappedValue: "paymentDate",
      width: 12,
      headerAlign: "left",
      align: "left",
      truncateText: true,
    },
    {
      headerName: "Paid by",
      mappedValue: "paidBy",
      width: 12,
      headerAlign: "left",
      align: "left",
      truncateText: true,
    },
    {
      headerName: "Paid Amount",
      mappedValue: "paidAmount",
      width: 12,
      headerAlign: "left",
      align: "left",
      truncateText: true,
      includeCurrenySymbol: true,
    },
    {
      headerName: "Invoices",
      mappedValue: "invoices",
      width: 10,
      headerAlign: "left",
      align: "left",
      truncateText: true,
      renderToolTip: true,
    },
    {
      headerName: "Credit Note Used",
      mappedValue: "creditNoteUsed",
      width: 16,
      headerAlign: "left",
      align: "left",
      truncateText: true,
    },
    {
      headerName: "Shortage Amount (if any)",
      headerParts: { top: "Shortage", bottom: "Amount (if any)" },
      mappedValue: "shortageAmount",
      width: 14,
      headerAlign: "left",
      align: "left",
      truncateText: true,
      includeCurrenySymbol: true,
    },
    {
      headerName: "Payment Approval Status",
      headerParts: { top: "Payment", bottom: "Approval Status" },
      mappedValue: "paymentApprovalStatus",
      width: 14,
      headerAlign: "left",
      align: "left",
      truncateText: true,
    },
  ],
  Actions: ["view"],
};

// Define ModalConfig type
export type ModalConfig = {
  type: "positive" | "success" | "negative" | "error";
  message: string | any[];
  leftButtonText?: string;
  rightButtonText?: string;
  onLeftButtonClick?: () => void;
  onRightButtonClick?: () => void;
  onClose?: () => void;
};

export const collectionStatusMapping = () => [
  {
    id: "UNPAID",
    dmsLabelColor: "#C44C36",
    dmsLabel: "Unpaid",
    dmsColor: "#FFFAF8",
  },
  {
    id: "PAID",
    dmsLabelColor: "#009847",
    dmsLabel: "Paid",
    dmsColor: "#EAFFF4",
  },
  {
    id: "PARTIALLY_PAID",
    dmsLabelColor: "#139D98",
    dmsLabel: "Partially Paid",
    dmsColor: "#E6FFFE",
  },
];

export const paymentApprovalStatusConfig = () => [
  {
    id: "pending",
    dmsLabelColor: "#CC911F",
    dmsLabel: "Pending",
    dmsColor: "#FFF9EF",
  },
  {
    id: "approved",
    dmsLabelColor: "#17B4A3",
    dmsLabel: "Approved",
    dmsColor: "#F0FBFF",
  },

  {
    id: "rejected",
    dmsLabelColor: "#C44C36",
    dmsLabel: "Rejected",
    dmsColor: "#FFFAF7",
  },
];

export const collectionsHomeScreenTableMetaData = () => {
  return {
    columns: [
      {
        headerName: "Outlet Code",
        mappedValue: "outletCode",
        cellRenderElement: (
          params: GridRenderCellParams<OutletCollectionDetails>
        ) => (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", gap: "6px" }}>
              <div>{params?.row?.outletCode}</div>
              {params?.row?.gstApplicable && (
                <div style={{ height: "12px", marginTop: "1px" }}>
                  <RenderSvg name="gstIcon" />
                </div>
              )}
            </div>
          </div>
        ),
        width: 18,
        truncateText: true,
      },
      {
        headerName: "Outlet Name",
        width: 20,
        mappedValue: "outletName",
        truncateText: true,
      },
      {
        headerName: "Type of Customer",
        headerParts: { top: "Type of", bottom: "Customer" },
        mappedValue: "outletType",
        width: 10,
        truncateText: true,
      },
      {
        headerName: "Credit Note Amount",
        headerParts: { top: "Credit Note", bottom: "Amount" },
        mappedValue: "creditNoteAmount",
        width: 12,
        // currencySymbol: "₹",
        includeCurrenySymbol: true,
        truncateText: true,
      },
      {
        headerName: "Balance Amount",
        headerParts: { top: "Balance", bottom: "Amount" },
        mappedValue: "creditAmount",
        width: 13,
        // currencySymbol: "₹",
        includeCurrenySymbol: true,
        truncateText: true,
      },
      {
        headerName: "Payment Approval Status",
        headerParts: { top: "Payment Approval", bottom: "Status" },
        mappedValue: "status",
        width: 15,

      },
    ],
    Actions: ["redirect"],
  };
};

// Define styles for the paymentMode badge based on the payment method
export const getPaymentModeStyles = (mode: string) => {
  if (mode.toLowerCase() === "cash") {
    return {
      backgroundColor: "#FAF3E8",
      color: "#CC9020",
    };
  } else if (mode.toLowerCase() === "upi") {
    return {
      backgroundColor: "#E7F4F8",
      color: "#17B4A3",
    };
  } else if (mode.toLowerCase() === "cheque") {
    return {
      backgroundColor: "#E8ECEF",
      color: "#2C3E50",
    };
  } else if (mode.toLowerCase() === "neft") {
    return {
      backgroundColor: "#E9F5E9",
      color: "#2E7D32",
    };
  }
  // Default styles for unknown payment modes
  return {
    backgroundColor: "#F0F0F0",
    color: "#000000",
  };
};


export interface PaymentStatusStyling {
  dmsLabelColor: string;
  dmsLabel: string;
  dmsColor: string;
}

export interface BaseOption {
  id: string;
  label: string;
}

export interface InvoiceOption extends BaseOption {
  paymentStatus?: string;
  statusStyling?: PaymentStatusStyling;
}

export interface PaymentDetail {
  mode: string;
  amount: number;
  transactionId?: string;
  chequeDate?: string;
  bankAccountNo?: string;
  bankName?: string;
  ifscCode?: string;
}


export const paymentModes: string[] = ["Cash", "UPI", "Cheque", "NEFT"];

export const PrettoSlider = styled(Slider)({
  color: "#24C6B1", // Progress color (track)
  height: 8,
  "& .MuiSlider-track": {
    border: "none",
    backgroundColor: "#24C6B1", // Explicitly set track color (progress)
  },
  "& .MuiSlider-rail": {
    backgroundColor: "#D9D9D9", // Background color of the entire slider
  },
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: "#fff",
    border: "2px solid currentColor",
    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
      boxShadow: "inherit",
    },
    "&::before": {
      display: "none",
    },
  },
  "& .MuiSlider-valueLabel": {
    lineHeight: 1.2,
    fontSize: 12,
    background: "unset",
    padding: 0,
    width: 32,
    height: 32,
    borderRadius: "50% 50% 50% 0",
    backgroundColor: "#24C6B1", // Match value label to progress color
    transformOrigin: "bottom left",
    transform: "translate(50%, -100%) rotate(-45deg) scale(0)",
    "&::before": { display: "none" },
    "&.MuiSlider-valueLabelOpen": {
      transform: "translate(50%, -100%) rotate(-45deg) scale(1)",
    },
    "& > *": {
      transform: "rotate(45deg)",
    },
  },
});


export interface PaymentDetail {
  mode: string;
  amount: number;
  transactionId?: string;
  chequeDate?: string;
  bankAccountNo?: string;
  bankName?: string;
  ifscCode?: string;
}

export interface CreditNote {
  id: string;
  amount: number;
  noteNumber: string;
    availableAmount: number;
  expired: boolean;
  endDate: string; 
}

export interface InvoiceOption {
  id: string;
  label: string;
  paymentStatus?: string;
  statusStyling?: {
    dmsLabelColor: string;
    dmsLabel: string;
    dmsColor: string;
  };
}

export interface Payment {
  mode: string;
  amount: number;
  transactionId?: string;
  chequeNo?: string;
  chequeDate?: string;
  bankName?: string;
  ifscCode?: string;
  creditNoteId?: string;
  payeeName?: string;
  bankAccountNumber?: string; // Used for NEFT
}

export interface Payload {
  outletCode: string;
  typeOfCustomer: string;
  totalAmount: string;
  payments: Payment[];
  invoiceNumber?: string;
  status: string;
  supplier?: string;
}

export interface PreparePayloadParams {
  paymentDetails: PaymentDetail[];
  creditNotes: CreditNote[];
  outletCode: string;
  type: string;
  selectedInvoice: InvoiceOption | null;
  useExpiredCreditNotes: boolean;
  totalAmount: number;
}

export interface PaymentCollectionPaymentDetail {
  shortageAmount: number;
  paymentMethod: string;
  idList: string[];
  paymentDate: string;
  paidAmount: number;
}

export interface PaymentCollectionRow {
  id: number;
  paymentDate: string;
  paidBy: string;
  paidAmount: string;
  invoices: string[];
  creditNoteUsed: string[];
  shortageAmount: string;
  paymentApprovalStatus: string;
  paymentDetails: PaymentCollectionPaymentDetail[];
  approvedPaidAmount: string;
  showEyeIcon?: boolean;
  creationTime: string;
}

export interface PaymentSummary {
  paidByCash: number;
  paidByUpi: number;
  paidByCheque: number;
  paidByNeft: number;
}

export const defaultOutletCollectionDetails: OutletCollectionDetails = {
  outletCode: "",
  outletName: "",
  outletType: "",
  creditLimit: 0,
  creditNoteAmount: 0,
  balanceAmount: 0,
  status: "",
  redirectIcon: "",
  gstApplicable: false,
  creditDueDate: undefined,
  creditStatus: ""
};

// Helper function to handle decimal precision for monetary calculations
export const roundToTwoDecimals = (num: number): number => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

// Helper function to compare decimal amounts with proper precision
export const isAmountGreater = (amount1: number, amount2: number): boolean => {
  return roundToTwoDecimals(amount1) > roundToTwoDecimals(amount2);
};


